<template>
  <div>
    <toastification-loading-content ref="loadingToast" />

    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters @show-modal="$root.$emit('bv::show::modal', $event)" :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters" />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters :tenant-filter.sync="tenantFilter" :tenant-options="tenantOptions"
          :user-filter.sync="userFilter" :user-options="userOptions" />
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card class="m-2" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select v-model="pagination.perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="pagination.perPageOptions" :clearable="false" class="per-page-selector d-inline-block mx-50"
              data-cy="per-page-selector" />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>



          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="pagination.searchQuery" class="d-inline-block mr-1"
                :placeholder="$t('table.search')" data-cy="search" />
              <b-button variant="primary" to="/alarm-rules/match-rules" v-if="$can('createRule', 'layout')">
                <span class="text-nowrap text-capitalize">
                  {{ `${$t("actions.add")} ${$tc("alarm_rules.title", 1)}` }}
                </span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table ref="refListTable" class="position-relative" :items="fetchList" responsive :fields="tableColumns"
        primary-key="rule_id" :sort-by.sync="pagination.sortBy" show-empty :empty-text="$t('no_record_found')"
        :sort-desc.sync="pagination.isSortDirDesc">
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #cell(color)="data">
          <div :id="`${data.item.id}+tooltip`" style="
              width: 100%;
              display: flex;
              justify-content: start;
              align-items: center;
            ">
            <div :style="{
              'background-color': data.item.color,
              'border-radius': '50px',
              height: '20px',
              width: '20px',
            }"></div>
          </div>
        </template>

        <template #head(actions)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" data-cy="actions" />
            </template>

            <b-dropdown-item :to="`/alarm-rules/match-rules/${data.item.id}`" v-if="$can('editrule', 'layout')">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50 text-capitalize">{{
                $t("actions.edit")
              }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="confirmDelete(data.item.id)" v-if="$can('deleterule', 'layout')">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50 text-capitalize">{{
                $t("actions.delete")
              }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>


      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <Pagination :totalList="pagination.totalList" :currentPage="pagination.currentPage"
              @updatePagination="update" :perPage="pagination.perPage" data-cy="pagination">
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
} from "bootstrap-vue";
import { ref } from '@vue/composition-api'
import vSelect from "vue-select";
import useList from "./useList";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import store from "@/store";
import Pagination from "@/components/Pagination.vue";

import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import Filters from "./Filters.vue";

export default {
  components: {
    BTooltip,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    Pagination,
    ToastificationLoadingContent,
    vSelect,
    FilterModal,
    ButtonFilters,
    Filters,
    BreadcrumbMenu
  },
  setup(_, context) {

    const loadingToast = ref(null);
    const searchQuery = ref("")


    // destructuring
    const { fetchList, tableColumns, pagination, dataMeta, refListTable, refetchData, tenantOptions, tenantFilter, userOptions, userFilter, filtersCount, clearSearchFilters,buildFilters, t } = useList();
    const toast = useToast();


    // Pagination update function
    const update = (data) => {
      pagination.currentPage = data;
    };

    // confirmDelete shows a modal to confir delete action
    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: context.parent.$i18n.t("messages.delete.title"),
        text: context.parent.$i18n.t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: context.parent.$i18n.t("actions.delete"),
        cancelButtonText: context.parent.$i18n.t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("alarm_rules/delete", id)
            .then((response) => {
              toast({
                component: ToastificationContent,
                props: {
                  title: response.message,
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
              self.refetchData();
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    return {
      update,
      searchQuery,
      loadingToast,
      confirmDelete,
      fetchList,
      tableColumns,
      pagination,
      dataMeta,
      refListTable,
      refetchData,
      tenantOptions,
      tenantFilter,
      userOptions,
      userFilter,
      filtersCount,
      clearSearchFilters,
      buildFilters,
      t,
    };
  },
};
</script>
